import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Stack, Table,Text  } from "@mantine/core";

import {
  TableEmptyRow,
  TableErrorRow,
  TableSkeletonRows,
} from "@components/Table";

import { formatDateToString } from "@utils/dateValidation";
import { renderLoadingErrorData } from "@utils/renderLoadingErrorData";

import { useTableColumnCount } from "@hooks/useTableColumnCount";

import { BillTransaction } from "@customTypes/billing";


interface TransactionsTableProps {
  data: BillTransaction[];
  isSuccess: boolean;
  isError: boolean;
  isLoading: boolean;
}

export const TransactionsTable = ({
  data,
  isError,
  isLoading,
  isSuccess,
}: TransactionsTableProps) => {
  const { t, i18n } = useTranslation();
  const { tableRef, columnCount } = useTableColumnCount();

  const transactionRows = useMemo(() => {
    return data?.map((item, index) => (
      <Table.Tr key={index}>
        <Table.Td>
          <Stack gap={5}>
            <Text fz={14}>{formatDateToString(item.date as Date)}</Text>
            <Text c="dimmed" fz={13}>
              {(item.date as Date).toLocaleTimeString(i18n.language, {
                hour: "2-digit",
                minute: "2-digit",
              })}
            </Text>
          </Stack>
        </Table.Td>
        <Table.Td>
          {Intl.NumberFormat(i18n.language, {
            minimumFractionDigits: 2,
          }).format(item.oldBalance as number)}
        </Table.Td>
        <Table.Td>{`${item.discount ?? 0}%`}</Table.Td>
        <Table.Td>
          {Intl.NumberFormat(i18n.language, {
            minimumFractionDigits: 2,
          }).format(item.newBalance as number)}
        </Table.Td>
        <Table.Td>
          {Intl.NumberFormat(i18n.language, {
            minimumFractionDigits: 2,
          }).format(item.amountPaid as number)}
        </Table.Td>
        <Table.Td tt="capitalize">{item.paymentMethod}</Table.Td>
        <Table.Td tt="capitalize">
          {item?.promoCode ? item.promoCode : "-"}
        </Table.Td>
      </Table.Tr>
    ));
  }, [data, i18n.language]);

  return (
    <Table
      ref={tableRef}
      withRowBorders={true}
      mt={"1rem"}
      miw={1000}
      withTableBorder
      highlightOnHover
      highlightOnHoverColor="var(--clr-gray-100)">
      <Table.Thead>
        <Table.Tr bg={"var(--clr-gray-100)"}>
          <Table.Th>{t("date")}</Table.Th>
          <Table.Th>{t("old-balance")}</Table.Th>
          <Table.Th>{t("discount")}</Table.Th>
          <Table.Th>{t("new-balance")}</Table.Th>
          <Table.Th>{t("amount-paid")}</Table.Th>
          <Table.Th>{t("method")}</Table.Th>
          <Table.Th>{t("promo-code")}</Table.Th>
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {renderLoadingErrorData({
          isLoading: isLoading,
          isError: isError,
          isSuccess: isSuccess,
          dataLength: data?.length || 0,
          emptyDataComponent: (
            <TableEmptyRow
              colSpan={columnCount}
              message={t("empty-transactions")}
            />
          ),
          errorComponent: (
            <TableErrorRow
              colSpan={columnCount}
              message={t("encountered-error")}
            />
          ),
          loadingComponent: (
            <TableSkeletonRows
              columns={columnCount}
              skeletonProps={{
                widths: ["md", "lg"],
              }}
            />
          ),
          dataComponent: transactionRows,
        })}
      </Table.Tbody>
    </Table>
  );
};
